import * as Sentry from "@sentry/react";
import {
  type Bridge,
  createActionBridge,
  createActionTelemetryMiddleware,
  createActionTimeoutMiddleware,
  createNavigateHandler,
  createParentBridge,
  NAVIGATE_ACTION_METHOD,
} from "@upsidelabs/miniapp-sdk";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Copy,
  FileQuestion,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useTheme } from "@/components/theme-provider";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { type AppStatus, useApp, useAppEmbed } from "@/hooks/api/use-apps";
import { usePageTitle } from "@/hooks/use-page-title";
import { createDownloadHandler } from "@/lib/miniapp-download-action";
import { DOWNLOAD_ACTION_METHOD } from "@/types/miniapp-download";

function getStatusBadge(status: AppStatus) {
  const config: Record<
    AppStatus,
    {
      label: string;
      variant: "secondary" | "team-emerald" | "team-amber" | "destructive";
    }
  > = {
    draft: { label: "Draft", variant: "secondary" },
    active: { label: "Active", variant: "team-emerald" },
    disabled: { label: "Disabled", variant: "destructive" },
    archived: { label: "Archived", variant: "team-amber" },
  };

  const { label, variant } = config[status] ?? {
    label: status,
    variant: "secondary",
  };
  return <Badge variant={variant}>{label}</Badge>;
}

export default function AppView() {
  const { slugId } = useParams<{ slugId: string }>();
  const navigate = useNavigate();
  const { effectiveTheme } = useTheme();
  const posthog = usePostHog();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<Bridge | null>(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  // Fetch app metadata and embed URL from API
  const {
    data: app,
    isLoading: isAppLoading,
    error: appError,
  } = useApp(slugId);

  const {
    data: embedInfo,
    isLoading: isEmbedLoading,
    error: embedError,
    refetch: refetchEmbed,
  } = useAppEmbed(slugId);

  // Derive state from API data
  const appExists = !!app;
  const appName = app?.display_name || slugId || "App";
  const appStatus = app?.status;
  const isLoading = isAppLoading || isEmbedLoading || iframeLoading;

  // Use embed URL directly - iframe only renders when we have a valid URL
  const iframeSrc = embedInfo?.embed_url ?? null;

  usePageTitle(appName);

  const isAllowedMiniAppOrigin = useCallback((origin: string) => {
    // Allow same-origin for local testing (placeholder files served from same origin)
    if (origin === window.location.origin) return true;

    try {
      const { protocol, hostname } = new URL(origin);
      // Allow production mini-app subdomains
      if (protocol === "https:" && hostname.endsWith(".upside-miniapps.com")) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  // Listen for overlay-state messages from iframe (e.g., when a modal opens)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Accept only allowed origins (mini-app subdomains or same-origin for local testing)
      if (!isAllowedMiniAppOrigin(event.origin)) {
        return;
      }

      if (!event.data || typeof event.data !== "object") {
        return;
      }

      const { type, visible } = event.data as {
        type?: unknown;
        visible?: unknown;
      };

      if (type === "overlay-state" && typeof visible === "boolean") {
        setShowOverlay(visible);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isAllowedMiniAppOrigin]);

  // Reset overlay state when iframe src changes
  useEffect(() => {
    setShowOverlay(false);
  }, []);

  // Send theme updates to iframe via postMessage.
  // Resolve target origin from iframeSrc (supports absolute mini-app URLs).
  const iframeTargetOrigin = useMemo(() => {
    if (!iframeSrc) return window.location.origin;
    try {
      return new URL(iframeSrc, window.location.origin).origin;
    } catch {
      return window.location.origin;
    }
  }, [iframeSrc]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: "theme-change", theme: effectiveTheme },
        iframeTargetOrigin,
      );
    }
  }, [effectiveTheme, iframeTargetOrigin]);

  // Timeout for slow loading - consider it an error after 30 seconds
  useEffect(() => {
    if (!iframeLoading) return;

    const timeout = setTimeout(() => {
      if (iframeLoading) {
        setHasError(true);
        setIframeLoading(false);
      }
    }, 30000);

    return () => clearTimeout(timeout);
  }, [iframeLoading]);

  // Reset iframe loading state when embed URL changes or embed fails
  useEffect(() => {
    if (embedInfo?.embed_url) {
      setIframeLoading(true);
      setHasError(false);
    } else if (embedError) {
      // Clear loading state when embed fails (no iframe will mount)
      setIframeLoading(false);
    }
  }, [embedInfo?.embed_url, embedError]);

  // Security: Warn if CSP is missing in production
  // CSP is defense-in-depth against XSS in miniapp iframes
  useEffect(() => {
    if (import.meta.env.PROD && embedInfo && !embedInfo.csp) {
      Sentry.addBreadcrumb({
        category: "security",
        message: "CSP missing for miniapp iframe",
        level: "warning",
        data: { miniapp_id: slugId },
      });
    }
  }, [embedInfo, slugId]);

  // Setup MiniApp SDK bridge when iframe is ready
  // The bridge enables RPC communication between parent and mini-app
  // biome-ignore lint/correctness/useExhaustiveDependencies: posthog is a stable reference from usePostHog hook
  useEffect(() => {
    const iframe = iframeRef.current;

    // Only create bridge when we have a valid iframe and src
    if (!iframe || !iframeSrc || iframeLoading) {
      return;
    }

    // Create the parent bridge for RPC communication
    const bridge = createParentBridge({
      targetOrigin: iframeTargetOrigin,
      scope: "upside-miniapp",
      version: 1,
      iframe,
    });

    bridgeRef.current = bridge;

    // Create action bridge with middleware support
    // ActionBridge wraps the bridge and handles cleanup via destroy()
    const actionBridge = createActionBridge(bridge);

    // Register middleware in order of execution:
    // 1. Telemetry - logs action denials to Sentry breadcrumbs
    actionBridge.use(
      createActionTelemetryMiddleware((event) => {
        if (import.meta.env.DEV) {
          console.warn(
            `[MiniApp Action] Denied: ${event.method} (${event.reason}) - ${event.message}`,
          );
        }
      }),
    );

    // Future middleware (implement when needed):
    // - capabilityCheckMiddleware: verify miniapp has capability for the action
    // - rateLimiterMiddleware: prevent action spam from misbehaving miniapps

    // 2. Timeout - abort actions that take too long (10s)
    actionBridge.use(createActionTimeoutMiddleware(10_000));

    // Register action handlers
    actionBridge.handle(
      NAVIGATE_ACTION_METHOD,
      createNavigateHandler({
        navigate,
        onNavigate: (params, target) => {
          // Track navigation events in PostHog for analytics
          posthog?.capture("miniapp_navigate", {
            miniapp_id: slugId,
            path: params.path,
            target,
            replace: params.replace,
          });
        },
        onBeforeNavigate: async () => true,
        onAfterNavigate: async () => {},
      }),
    );

    actionBridge.handle(
      DOWNLOAD_ACTION_METHOD,
      createDownloadHandler({
        maxRows: 100000,
        onDownload: (params, rowCount) => {
          // Track download events in PostHog for analytics
          posthog?.capture("miniapp_download", {
            miniapp_id: slugId,
            filename: params.filename,
            row_count: rowCount,
            column_count: params.columns.length,
          });
        },
        onBeforeDownload: async () => true,
        onAfterDownload: async () => {},
      }),
    );

    // Cleanup on unmount or when dependencies change
    return () => {
      actionBridge.destroy();
      bridgeRef.current = null;
    };
  }, [iframeSrc, iframeTargetOrigin, iframeLoading, navigate, slugId]);

  const handleIframeLoad = () => {
    setIframeLoading(false);
    setHasError(false);
  };

  const handleIframeError = () => {
    setIframeLoading(false);
    setHasError(true);
  };

  const handleRetry = () => {
    setIframeLoading(true);
    setHasError(false);
    // Refetch embed URL to get a fresh token
    refetchEmbed();
  };

  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Show loading state while fetching app metadata
  if (isAppLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="shrink-0 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/apps">Apps</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Loading...</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Loading app...
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Show 404 state for invalid slugs or API errors
  if (!appExists || appError) {
    return (
      <div className="flex flex-col h-full">
        {/* Breadcrumb navigation */}
        <div className="shrink-0 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/apps">Apps</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Not Found</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* 404 content */}
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="rounded-full bg-muted p-6">
              <FileQuestion className="h-12 w-12 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">
                App Not Found
              </h2>
              <p className="text-sm text-muted-foreground max-w-md">
                The app "{slugId}" doesn't exist or may have been removed.
              </p>
            </div>
            <Button asChild variant="outline" className="bg-card">
              <Link to="/apps">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Apps
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if embed URL couldn't be fetched (e.g., non-embeddable status)
  const isNotEmbeddable = embedError && !isEmbedLoading;

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb navigation with actions */}
      <div className="shrink-0 py-4 flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/apps">Apps</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <div className="flex items-center gap-2">
                <BreadcrumbPage>{appName}</BreadcrumbPage>
                {appStatus && getStatusBadge(appStatus)}
              </div>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyLink}
            className="bg-card"
          >
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copy Link
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Modal backdrop overlay - shown when iframe reports a modal is open */}
      {showOverlay && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300" />
      )}

      {/* Iframe container with loading/error states - breaks out of main padding */}
      <div className="flex-1 min-h-0 relative overflow-hidden -mx-4 -mb-2 w-[calc(100%+2rem)]">
        {/* Loading overlay - fades out and slides down when done */}
        <div
          className={`absolute inset-0 flex items-center justify-center z-10 transition-all duration-200 ease-in ${
            isLoading && !hasError
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4 pointer-events-none"
          }`}
        >
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Loading app...
            </span>
          </div>
        </div>

        {/* Error overlay - shows when iframe fails or app is not embeddable */}
        {(hasError || isNotEmbeddable) && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-full bg-destructive/10 p-4">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {isNotEmbeddable ? "App Not Available" : "Failed to load app"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isNotEmbeddable
                    ? "This app is not available for viewing. It may be disabled or archived."
                    : "The app content could not be loaded. Please try again."}
                </p>
              </div>
              {!isNotEmbeddable && (
                <Button variant="outline" size="sm" onClick={handleRetry}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              )}
              <Button asChild variant="outline" size="sm">
                <Link to="/apps">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Apps
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Iframe - only renders when we have a valid embed URL */}
        {iframeSrc && (
          <iframe
            ref={iframeRef}
            className="w-full h-full z-50 relative"
            style={{
              opacity: isLoading ? 0 : 1,
              transform: isLoading ? "translateY(16px)" : "translateY(0)",
              transition: isLoading
                ? "opacity 0ms, transform 0ms" // Instant hide when loading starts
                : "opacity 300ms ease-out 150ms, transform 300ms ease-out 150ms", // Delayed fade in after loading overlay exits
            }}
            title={appName}
            src={iframeSrc}
            sandbox="allow-scripts allow-same-origin"
            allow="camera 'none'; microphone 'none'; geolocation 'none'; fullscreen 'none'; payment 'none'; clipboard-read 'none'; clipboard-write 'none'; display-capture 'none'; xr-spatial-tracking 'none'"
            // csp is an experimental attribute (Chrome/Edge only), not in React's types
            // See: https://developer.mozilla.org/en-US/docs/Web/API/HTMLIFrameElement/csp
            // Server returns null for local dev, policy string for prod/staging
            {...(embedInfo?.csp
              ? ({
                  csp: embedInfo.csp,
                } as React.IframeHTMLAttributes<HTMLIFrameElement>)
              : undefined)}
            referrerPolicy="no-referrer"
            loading="lazy"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        )}
      </div>
    </div>
  );
}
